/**
 * Utilidades para manejo de monedas
 * Funciones para formatear, convertir y agrupar montos por moneda
 */

import { CURRENCIES, CURRENCY_OPTIONS } from "../store/controlCashStore";

/**
 * Formatea un monto según su moneda
 * @param {number} amount - Monto a formatear
 * @param {string} currency - Código de moneda (PEN, USD, etc.)
 * @param {boolean} hideValue - Si true, muestra "••••" en lugar del monto
 * @returns {string} Monto formateado con símbolo de moneda
 */
export const formatAmountByCurrency = (amount, currency = "PEN", hideValue = false) => {
  if (hideValue) return "••••";
  
  const currencyInfo = CURRENCY_OPTIONS.find(opt => opt.code === currency) || CURRENCY_OPTIONS[0];
  const symbol = currencyInfo?.symbol || CURRENCIES[currency] || "S/";
  
  // Valores reales: 2 decimales para todas las monedas
  const formatted = new Intl.NumberFormat("es-PE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
  
  return `${symbol} ${formatted}`;
};

/**
 * Convierte un monto de una moneda a otra usando tipos de cambio
 * @param {number} amount - Monto a convertir
 * @param {string} fromCurrency - Moneda origen
 * @param {string} toCurrency - Moneda destino
 * @param {object} exchangeRates - Objeto con tipos de cambio { PEN: 3.75, USD: 1, ... }
 * @returns {number} Monto convertido
 */
export const convertCurrency = (amount, fromCurrency, toCurrency, exchangeRates = {}) => {
  if (!amount || isNaN(amount)) return 0;
  if (fromCurrency === toCurrency) return amount;
  
  // Los tipos de cambio están en formato: 1 USD = X en cada moneda
  const fromRate = exchangeRates[fromCurrency] || 1;
  const toRate = exchangeRates[toCurrency] || 1;
  
  // Si no hay tipos de cambio, no convertir
  if (!fromRate || !toRate) return amount;
  
  // Convertir a USD primero, luego a la moneda destino
  const amountInUsd = amount / fromRate;
  const convertedAmount = amountInUsd * toRate;
  
  return convertedAmount;
};

/**
 * Agrupa transacciones por moneda y calcula totales
 * @param {Array} transactions - Array de objetos con { amount, currency }
 * @returns {object} Objeto con totales por moneda { PEN: 100, USD: 50 }
 */
export const groupByCurrency = (transactions = []) => {
  const grouped = {};
  
  transactions.forEach(transaction => {
    const currency = transaction.currency || "PEN";
    const amount = parseFloat(transaction.amount) || 0;
    
    if (!grouped[currency]) {
      grouped[currency] = 0;
    }
    grouped[currency] += amount;
  });
  
  return grouped;
};

/**
 * Calcula el total estimado en una moneda base
 * @param {Array} transactions - Array de objetos con { amount, currency }
 * @param {string} baseCurrency - Moneda base para el total (ej: "PEN")
 * @param {object} exchangeRates - Tipos de cambio
 * @returns {number} Total estimado en la moneda base
 */
export const calculateTotalInBaseCurrency = (transactions = [], baseCurrency = "PEN", exchangeRates = {}) => {
  return transactions.reduce((total, transaction) => {
    const amount = parseFloat(transaction.amount) || 0;
    const currency = transaction.currency || "PEN";
    
    if (currency === baseCurrency) {
      return total + amount;
    }
    
    const converted = convertCurrency(amount, currency, baseCurrency, exchangeRates);
    return total + converted;
  }, 0);
};

/**
 * Obtiene el símbolo de una moneda
 * @param {string} currency - Código de moneda
 * @returns {string} Símbolo de la moneda
 */
export const getCurrencySymbol = (currency = "PEN") => {
  const currencyInfo = CURRENCY_OPTIONS.find(opt => opt.code === currency);
  return currencyInfo?.symbol || CURRENCIES[currency] || "S/";
};

/**
 * Obtiene el label completo de una moneda
 * @param {string} currency - Código de moneda
 * @returns {string} Label completo (ej: "Soles (Perú)")
 */
export const getCurrencyLabel = (currency = "PEN") => {
  const currencyInfo = CURRENCY_OPTIONS.find(opt => opt.code === currency);
  return currencyInfo?.label || currency;
};

/**
 * Valida si un código de moneda es válido
 * @param {string} currency - Código de moneda a validar
 * @returns {boolean} True si es válido
 */
export const isValidCurrency = (currency) => {
  return CURRENCY_OPTIONS.some(opt => opt.code === currency);
};
